import React from "react";
import { Popup } from "react-map-gl/mapbox";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

import styles from "./historicalHazardPopup.module.css";

const EMPTY = "—";

const value = (properties, key) => {
  const v = properties?.[key];
  return v === undefined || v === null || v === "" ? EMPTY : v;
};

// Concise, per-hazard field set. Each entry is [translationKey, resolver].
const FIELDS_BY_RISK = {
  earthquake: [
    ["risksMap.historical.magnitude", (p) => value(p, "MAGNITUDE")],
    ["risksMap.historical.depth", (p) => value(p, "DEPTH")],
    ["risksMap.historical.date", (p) => value(p, "DATE")],
  ],
  flood: [
    ["risksMap.historical.date", (p) => value(p, "date_of_commencement")],
    ["risksMap.historical.rainfallHeight", (p) => value(p, "rainfall_height")],
  ],
  fire: [
    ["risksMap.historical.date", (p) => value(p, "fire_date")],
    ["risksMap.historical.area", (p) => value(p, "area_ha")],
  ],
};

// Optional title line shown above the fields for some hazards.
const TITLE_BY_RISK = {
  flood: (p) => {
    const name = p?.flood_event_name;
    return name === undefined || name === null || name === "" ? null : name;
  },
};

// Grouped flood features carry every event's original properties as a JSON
// string under `events` (see useHistoricalFloodEvents).
const parseGroupedEvents = (properties) => {
  if (!properties?.events) {
    return null;
  }
  try {
    const events = JSON.parse(properties.events);
    return Array.isArray(events) && events.length > 0 ? events : null;
  } catch {
    return null;
  }
};

function EventDetails({ activeRisk, properties, t }) {
  const fields = FIELDS_BY_RISK[activeRisk] || [];
  const title = TITLE_BY_RISK[activeRisk]?.(properties);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {title && (
        <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
          {title}
        </Typography>
      )}
      {fields.map(([labelKey, resolve]) => (
        <Box
          key={labelKey}
          sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}
        >
          <Typography variant="caption" color="text.secondary">
            {t(labelKey)}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {String(resolve(properties))}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function HistoricalHazardPopup({ activeRisk, feature }) {
  const { t } = useTranslation();

  if (!feature) {
    return null;
  }

  const properties = feature.properties || {};
  // Flood carries its grouped events as a JSON string on the feature; other
  // hazards (fire) pass an explicit `events` array of every polygon under the
  // cursor. Either way we render the scrollable list only when there's >1 event.
  const groupedEvents =
    activeRisk === "flood"
      ? parseGroupedEvents(properties)
      : feature.events && feature.events.length > 1
        ? feature.events
        : null;

  return (
    <Popup
      longitude={feature.longitude}
      latitude={feature.latitude}
      closeButton={false}
      closeOnClick={false}
      anchor="bottom"
      maxWidth="280px"
      className={styles.popup}
    >
      {groupedEvents ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxHeight: 240,
            overflowY: "auto",
            pr: groupedEvents.length > 1 ? 2 : 0,
          }}
        >
          {groupedEvents.length > 1 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {t(
                activeRisk === "fire"
                  ? "risksMap.historical.fireEventsCount"
                  : "risksMap.historical.floodEventsCount",
                {
                  count: groupedEvents.length,
                },
              )}
            </Typography>
          )}
          {groupedEvents.map((eventProperties, index) => (
            <Box
              key={index}
              sx={
                index > 0
                  ? { borderTop: "1px solid", borderColor: "divider", pt: 1 }
                  : undefined
              }
            >
              <EventDetails
                activeRisk={activeRisk}
                properties={eventProperties}
                t={t}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <EventDetails activeRisk={activeRisk} properties={properties} t={t} />
      )}
    </Popup>
  );
}
